/**
 * Products component for Manatee Labs website.
 * Follows the design specifications from manatee-labs-full-spec.md
 */
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ProductsData from "../../data/productsData.json";
import "./products.css";
import { Voicemail, Settings } from "lucide-react";

interface Product {
  id: string;
  title: string;
  status: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  icon: string;
}

interface ProductsDataType {
  headline: string;
  subheadline: string;
  products: Product[];
}

export const Products = () => {
  const [productsData, setProductsData] = useState<ProductsDataType>({
    headline: "",
    subheadline: "",
    products: []
  });
  
  useEffect(() => {
    setProductsData(ProductsData);
  }, []);

  // Animation variants for fade-in effect
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Render icon based on icon name
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'voicemail':
        return <Voicemail size={48} color="#173fa3" />;
      case 'settings':
        return <Settings size={48} color="#173fa3" />;
      default:
        return null;
    }
  };

  return (
    <div id="products">
      <div className="container">
        <motion.div 
          className="products-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="products-title">{productsData.headline}</h2>
          <p className="products-subtitle">{productsData.subheadline}</p>
        </motion.div>

        <div className="products-grid">
          {productsData.products.map((product, index) => (
            <motion.div 
              className="product-card"
              key={product.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    duration: 0.6,
                    delay: index * 0.2
                  }
                }
              }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)"
              }}
            >
              <div className="product-icon">
                {renderIcon(product.icon)}
              </div>
              <div className="product-content">
                <h3 className="product-title">
                  {product.title}
                  {product.status && (
                    <span className="product-status">{product.status}</span>
                  )}
                </h3>
                <p className="product-description">{product.description}</p>
                <motion.a 
                  href={product.ctaLink} 
                  className="product-cta"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {product.ctaText}
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
